import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTableModule } from '@angular/material/table';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { TableComponent } from '../table/table.component';
import { ApiService } from '../../Services/api';
import { ApiConstants } from '../../Services/api/ApiConstants';
import { AlertService } from '../../Services/notifications/alert.service';
import { TokenService } from '../../Services/auth';

interface TaxAgent {
  tin: string;
  name: string;
  location: string;
  email: string;
}

@Component({
  selector: 'app-search-tax-agents',
  imports: [CommonModule, TableComponent, FormsModule, MatIconModule, MatButtonModule, ReactiveFormsModule, MatTableModule, MatFormFieldModule, MatInputModule, MatRadioModule, MatTooltipModule],
  templateUrl: './search-tax-agents.component.html',
  styleUrls: ['./search-tax-agents.component.scss']
})
export class SearchTaxAgentsComponent implements OnInit {
  searchValue: string = '';
  searchDisabled: boolean = false;
  searchResults: TaxAgent | null = null;
  showResults: boolean = false;
  showContent: boolean = false;

  responseData: any;
  tableData: any;
  agentForm!: FormGroup;
  displayedColumns: string[] = [];
  dataSource: any[] = [];
  selectedLevel: string | null = null;
  @ViewChild('searchInputRef') searchInputRef!: ElementRef<HTMLInputElement> | null;
  infoColumns: string[] = [];
  showServiceTable: boolean = false;
  constructor(private apiService: ApiService, private tokenService: TokenService, private fb: FormBuilder, private dialog: MatDialog, private cdr: ChangeDetectorRef, private alertService: AlertService) { }

  ngOnInit(): void {
    this.agentForm = this.fb.group({});

  }

  getFilteredHeadings(): string[] {
    if (!this.tableData || !this.tableData.tableHeadings) return [];
    return this.tableData.tableHeadings.filter((h: string) => h !== 'Id');
  }

  getFilteredData(): string[] {
    if (!this.tableData || !this.tableData.tableData || !this.tableData.tableHeadings) return [];
    const firstRow = this.tableData.tableData && this.tableData.tableData.length ? this.tableData.tableData : [];
    return firstRow?.filter((_: string, i: number) => this.tableData.tableHeadings[i] !== 'Id');
  }

  onSearch(): void {
    // console.log('Search initiated with value:', this.searchValue, this.tokenService.getUserProfile().id, typeof this.searchValue, typeof this.tokenService.getUserProfile().id);
    if(this.searchValue === this.tokenService.getUserProfile().id) {
      this.alertService.show('You cant assign yourself as your tax agent', { type: 'error', duration: 5000 });
      return;
    }
    const term = (this.searchValue || '').toString().trim();
    if (!term || this.searchDisabled) {
      return;
    }

    this.alertService.show('Searching for tax agents...', { type: 'info', duration: 2000 });
    this.tableData = [];
    // console.log("Searching for tax agents with BIN:", this.searchValue);
    const endpoint = `${ApiConstants.API_GET_Taxpayer_SearchTaxAgents}${encodeURIComponent(this.searchValue || '')}`;
    this.apiService.get<any>(endpoint).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.tableData = response.data['searchResults'];
          this.responseData = response.data['levels']
          console.log('API Response Data:', this.tableData);

          this.displayedColumns = this.responseData.tableHeadings.map((h: string) => this.getFormControlName(h));
          this.infoColumns = (this.responseData?.tableHeadings ?? [])
            .filter((h: string) => this.hasInfoIcon(h));
          this.dataSource = this.responseData.tableData.map((row: any[]) => {
            const rowObj: any = {};
            this.responseData.tableHeadings.forEach((heading: string, index: number) => {
              rowObj[this.getFormControlName(heading)] = row[index];
            });
            return rowObj;
          });

          this.responseData.tableHeadings.forEach((heading: any) => {
            const key = this.getFormControlName(heading);
            this.agentForm.addControl(key, new FormControl(''));
          });
          this.cdr.markForCheck();
          // console.log('Table Data:', this.tableData);
          if (this.tableData && this.tableData['tableData'].length > 0) {
            this.showResults = true;
            this.showContent = false;
            // disable searching once result is shown
            this.searchDisabled = true;
          } else {
            this.showResults = false;
            this.showContent = true;
          }

        }
      },
      error: (error) => {
        console.error('Error fetching data from API:', error);
        try {
          this.clearText()
          this.alertService.show(error.error?.message || 'Unable to fetch tax agents', { type: 'error', duration: 5000 });
        } catch (e) { /* ignore */ }
      }
    });

  }

  clearText() {
    this.onCancel()
    this.showContent = false;

  }
  search() { }

  allowOnlyNumbers(event: KeyboardEvent) {
    try {
      const allowed = ['Backspace', 'ArrowLeft', 'ArrowRight', 'Delete', 'Tab', 'Enter', 'Home', 'End'];
      if (allowed.includes(event.key)) return;
      if (event.ctrlKey || event.metaKey) return; // allow copy/paste/select all shortcuts
      if (!/^\d$/.test(event.key)) {
        event.preventDefault();
      }
    } catch (e) { /* ignore */ }
  }

  /** Strip any non-digit characters from the model on input */
  sanitizeNumericInput(event: Event) {
    try {
      const cleaned = (this.searchValue || '').toString().replace(/\D+/g, '');
      if (this.searchValue !== cleaned) {
        this.searchValue = cleaned;
      }
    } catch (e) { /* ignore */ }
  }

  handlePaste(event: ClipboardEvent) {
    try {
      const paste = (event.clipboardData && event.clipboardData.getData('text')) || '';
      const digits = paste.replace(/\D+/g, '');
      if (!digits) {
        event.preventDefault();
        return;
      }
      event.preventDefault();
      const el = this.searchInputRef && (this.searchInputRef.nativeElement as HTMLInputElement);
      if (el) {
        const start = el.selectionStart || 0;
        const end = el.selectionEnd || 0;
        const current = el.value || '';
        const newVal = current.slice(0, start) + digits + current.slice(end);
        this.searchValue = newVal.replace(/\D+/g, '');
      } else {
        this.searchValue = digits;
      }
    } catch (e) { /* ignore */ }
  }

  searchIconClicked() {
    const hasValue = (this.searchValue || '').toString().trim().length > 0;
    if (hasValue) {
      this.clearText();
    }
  }

  onCancel(): void {
    this.showResults = false;
    this.showContent = false;
    this.searchResults = null;
    this.searchValue = '';
    this.selectedLevel = null;
    // re-enable searching on cancel
    this.searchDisabled = false;

    // Clear table and form state
    this.tableData = [];
    this.responseData = null;
    this.displayedColumns = [];
    this.dataSource = [];
    if (this.agentForm) {
      try {
        this.agentForm.reset();
        this.agentForm = this.fb.group({});
      } catch (e) { /* ignore */ }
    }

    // focus the search input after clearing and scroll into view (robust)
    try {
      const el = this.searchInputRef && this.searchInputRef.nativeElement;
      const target = el || document.querySelector('.search-input') as HTMLInputElement | null;
      if (target) {
        setTimeout(() => {
          try {
            target.focus();
            // Try to find fixed header heights to offset the scroll
            const headerHeight = (document.querySelector('#otb-header') as HTMLElement)?.clientHeight || 0;
            const breadcrumbHeight = (document.querySelector('.breadcrumb-ul') as HTMLElement)?.clientHeight || 0;
            const additionalOffset = 20; // small breathing room

            const viewportWidth = window.innerWidth || document.documentElement.clientWidth;

            // Compute element's absolute top
            const rect = target.getBoundingClientRect();
            const absoluteTop = rect.top + window.pageYOffset;

            // Compute offset: header + breadcrumb + extra; allow larger offset on very wide screens
            let offset = headerHeight + breadcrumbHeight + additionalOffset;
            if (viewportWidth >= 1920) offset += 200;
            else if (viewportWidth >= 1280) offset += 100;

            const scrollToPos = Math.max(absoluteTop - offset, 0);

            window.scrollTo({ top: scrollToPos, behavior: 'smooth' });
          } catch (err) {
            // fallback: try native scrollIntoView
            try { (target as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) { }
          }
        }, 60);
      }
    } catch (e) { /* ignore */ }
  }

  // Robust scroll helper: scrolls the element into view within the nearest scrollable ancestor
  private scrollElementIntoView(target: HTMLElement) {
    if (!target) return;
    try {
      const getScrollableParent = (element: HTMLElement | null): HTMLElement | null => {
        let el: HTMLElement | null = element;
        while (el && el !== document.body && el !== document.documentElement) {
          const style = window.getComputedStyle(el);
          const overflowY = style.overflowY || style.overflow;
          if (overflowY && (overflowY === 'auto' || overflowY === 'scroll')) return el;
          el = el.parentElement;
        }
        return document.scrollingElement as HTMLElement || document.documentElement;
      };

      const scrollParent = getScrollableParent(target.parentElement as HTMLElement | null);

      if (scrollParent && scrollParent !== document.documentElement && scrollParent !== document.body) {
        // Scroll within the parent container so the element is centered
        const parentRect = scrollParent.getBoundingClientRect();
        const elRect = target.getBoundingClientRect();
        const offsetTop = elRect.top - parentRect.top + scrollParent.scrollTop - (parentRect.height / 2) + (elRect.height / 2);
        scrollParent.scrollTo({ top: offsetTop, behavior: 'smooth' });
      } else {
        // Scroll the page and account for fixed header/breadcrumb offsets
        const headerHeight = document.querySelector('#otb-header')?.clientHeight || 0;
        const breadcrumbHeight = document.querySelector('.breadcrumb-ul')?.clientHeight || 0;
        const base = 30;
        const largeScreenExtra = 260;
        let extra = 0;
        if (window.innerWidth >= 1920) extra = largeScreenExtra;
        else if (window.innerWidth >= 1280) extra = Math.floor(largeScreenExtra / 2);
        const offset = headerHeight + breadcrumbHeight + base + extra;

        const rect = target.getBoundingClientRect();
        const absoluteTop = rect.top + window.pageYOffset;
        const scrollToPosition = Math.max(0, absoluteTop - offset);
        window.scrollTo({ top: scrollToPosition, behavior: 'smooth' });
      }
    } catch (e) {
      // ignore scrolling errors
    }
  }

  onAppointAgent(): void {
    console.log('Submitting form with values:', this.agentForm.value, 'Selected level:', this.selectedLevel);
    const { ['si.no']: _, service, ...filteredFormData } = this.agentForm.value || {};

    // ensure selectedLevel is reflected in filteredFormData if present
    if (this.selectedLevel) filteredFormData[this.selectedLevel] = true;

    // derive values from the first row of tableData (search result)
    const firstRow = this.tableData && this.tableData.tableData && this.tableData.tableData.length ? this.tableData.tableData : [];
    const [bin, name, location, email] = firstRow;
    const headings = (this.responseData && this.responseData.tableHeadings) || [];
    const rowMap: Record<string, any> = {};
    headings.forEach((heading: string, idx: number) => {
      rowMap[this.getFormControlName(heading)] = firstRow[idx];
    });

    let assignedLevel = '';
    try {
      for (const k of Object.keys(filteredFormData || {})) {
        if (/^level\s*\d+$/i.test(k) || /^level\d+$/i.test(k)) {
          const v = filteredFormData[k];
          if (v === true || String(v).toLowerCase() === 'true' || (typeof v === 'string' && v.trim() !== '')) {
            assignedLevel = k.toLowerCase().replace(/\s+/g, '');
            break;
          }
        }
      }
      if (!assignedLevel && this.selectedLevel) assignedLevel = this.selectedLevel;
      if (!assignedLevel) {
        const assignedText = (rowMap['assignedlevels'] || rowMap['assignedlevel'] || rowMap['assignedlevels'] || '').toString();
        if (/level\s*1/i.test(assignedText)) assignedLevel = 'level1';
        else if (/level\s*2/i.test(assignedText)) assignedLevel = 'level2';
        else if (/level\s*3/i.test(assignedText)) assignedLevel = 'level3';
      }
    } catch (e) {
      assignedLevel = this.selectedLevel || '';
    }

    if (!assignedLevel) {
      assignedLevel = 'level1';
    }

    const payload = {
      bin,
      name,
      location,
      email,
      assignedLevel
    };

    console.log('Final payload to send:', payload, this.tableData);
    const updateEndpoint = ApiConstants.API_PUT_Taxpayer_UpdateLevel;
    this.apiService.put<any>(updateEndpoint, { payload }).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          // console.log('Level updated successfully:', response.data);
          this.cdr.markForCheck();
          this.onCancel();
          // ensure search is enabled after successful submit
          this.searchDisabled = false;
          try {
            this.alertService.show(response.data.message, { type: 'success', duration: 3000 });
          } catch (e) { /* ignore */ }
        }
      },
      error: (error) => {
        console.error('Error updating level:', error);
        try {
          // this.clearText();
          this.alertService.show(error.error || 'Unable to update level', { type: 'error', duration: 5000 });
          // this.cdr.markForCheck();
        } catch (e) { /* ignore */ }
      }
    });

  }

  getFormControlName(heading: string): string {
    return heading.toLowerCase().replace(/\s+/g, '');
  }

  hasInfoIcon(heading: string): boolean {
    const normalizedHeading = heading.toLowerCase();
    return normalizedHeading.includes('level 1') ||
      normalizedHeading.includes('level 2') ||
      normalizedHeading.includes('level 3');
  }

  getInfoColumns(): string[] {
    try {
      const headings = this.responseData && this.responseData.tableHeadings ? this.responseData.tableHeadings : [];
      return headings.filter((h: string) => this.hasInfoIcon(h));
    } catch (e) {
      return [];
    }
  }

}

